import { useState } from "react";
import { LogOut, Inbox, AlertCircle } from "lucide-react";
import Reveal from "../components/Reveal";
import { MessageRow, isStale } from "../components/admin/MessageRow";
import { useAdminMessages } from "../hooks/useAdminMessages";

const AdminLogin = ({ onLogin, error, loading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  return (
    <section className="max-w-md mx-auto px-4 pt-20 md:pt-32 pb-24">
      <Reveal className="card-flat p-8 md:p-12">
        <div className="overline mb-4">Area riservata</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tightest">
          Pannello messaggi
        </h1>
        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-login-form">
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email"
              className="w-full bg-transparent border-0 border-b border-ink-200 focus:border-ink-900 outline-none py-3 font-display transition-colors"
              placeholder="admin@..."
            />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              className="w-full bg-transparent border-0 border-b border-ink-200 focus:border-ink-900 outline-none py-3 font-display transition-colors"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p className="text-[13px] text-red-600" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="btn-pill w-full justify-center disabled:opacity-60"
          >
            {loading ? "Accesso…" : "Accedi"}
          </button>
        </form>
      </Reveal>
    </section>
  );
};

export default function Admin() {
  const { authed, messages, loading, error, login, logout, markRead, remove, reply } =
    useAdminMessages();

  if (authed === null) {
    return (
      <section className="max-w-md mx-auto px-4 pt-32 pb-24 text-center overline" data-testid="admin-checking">
        Verifica sessione…
      </section>
    );
  }

  if (!authed) return <AdminLogin onLogin={login} error={error} loading={loading} />;

  const unread = messages.filter((m) => !m.read).length;
  const stale = messages.filter(isStale).length;

  return (
    <section className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-14 md:pt-20 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-12">
        <div>
          <div className="overline mb-3">Area riservata</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tightest">
            Messaggi
          </h1>
          <p className="mt-2 text-[14px] text-ink-500" data-testid="admin-messages-count">
            {messages.length} totali · {unread} da leggere
          </p>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-btn"
          className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-ink-200 hover:border-ink-900 text-[11px] font-mono tracking-[0.14em] uppercase transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} /> Esci
        </button>
      </div>

      {stale > 0 && (
        <div
          className="mb-6 md:mb-8 flex items-start gap-3 p-4 md:p-5 bg-amber-50 border border-amber-300 rounded-sm"
          data-testid="admin-stale-banner"
        >
          <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.6} />
          <div>
            <p className="text-[14px] text-amber-900 font-medium">
              {stale === 1
                ? "1 messaggio è in attesa da più di 2 giorni."
                : `${stale} messaggi sono in attesa da più di 2 giorni.`}
            </p>
            <p className="text-[12px] text-amber-800 mt-0.5">
              Un promemoria via email viene inviato automaticamente a info@mysteriousstew.com.
            </p>
          </div>
        </div>
      )}

      {messages.length === 0 ? (
        <div
          className="card-flat p-12 md:p-20 text-center"
          data-testid="admin-empty-state"
        >
          <Inbox className="w-8 h-8 mx-auto text-ink-300" strokeWidth={1.2} />
          <p className="mt-4 text-ink-500">Nessun messaggio ricevuto.</p>
        </div>
      ) : (
        <ul className="space-y-4 md:space-y-5" data-testid="admin-messages-list">
          {messages.map((m) => (
            <MessageRow
              key={m.id}
              message={m}
              onMarkRead={markRead}
              onDelete={remove}
              onReply={reply}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
